import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../services/authService';
import { PasswordInput } from '../components/PasswordInput';

export const ResetPassword: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    code: '',
    new_password: '',
    new_password2: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Récupérer l'email depuis la navigation state
    if (location.state?.email) {
      setFormData(prev => ({ ...prev, email: location.state.email }));
    }
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.new_password !== formData.new_password2) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);

    try {
      await authService.resetPassword(
        formData.email,
        formData.code,
        formData.new_password,
        formData.new_password2
      );
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative bg-gradient-to-br from-white via-green-50/30 to-green-100/50 overflow-hidden">
      {/* Motifs décoratifs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-green-200/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-green-300/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-green-100/30 rounded-full blur-2xl"></div>
      </div>

      {/* Logo retour accueil en haut à gauche */}
      <div className="relative z-20 fixed top-4 left-4">
        <Link
          to="/"
          className="group flex items-center"
        >
          <div className="w-14 h-14 rounded-full border-[3px] border-green-600 shadow-xl overflow-hidden transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 bg-white p-1.5">
            <img
              src="/logo.jpg"
              alt="Retour à l'accueil"
              className="w-full h-full rounded-full object-contain"
              style={{ objectPosition: 'center center' }}
            />
          </div>
        </Link>
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8 py-12">
        <div className="w-full max-w-5xl">
          {/* Layout desktop avec logo à gauche */}
          <div className="hidden lg:flex items-center gap-12 w-full">
            {/* Logo à gauche - desktop uniquement */}
            <div className="flex-shrink-0">
              <div className="w-96 h-96 rounded-full border-[3px] border-green-600 shadow-2xl overflow-hidden transform hover:scale-110 hover:rotate-3 transition-all duration-300 bg-white p-5 animate-pulse hover:animate-bounce">
                <img
                  src="/logo.jpg"
                  alt="Hanafia Logo"
                  className="w-full h-full rounded-full object-contain"
                  style={{ objectPosition: 'center center' }}
                />
              </div>
            </div>

            {/* Formulaire à droite */}
            <div className="flex-1 max-w-md ml-auto">
              <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-green-100/50 p-8 sm:p-10">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-500 to-green-700 rounded-2xl mb-6 shadow-lg">
                    <span className="text-4xl">🔐</span>
                  </div>
                  <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-green-700 via-green-600 to-green-800 bg-clip-text text-transparent mb-3">
                    Nouveau mot de passe
                  </h1>
                  <p className="text-gray-600 text-lg">
                    Entrez le code reçu et votre nouveau mot de passe
                  </p>
                </div>

                {success ? (
                  <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 text-center">
                    <div className="text-5xl mb-4">✅</div>
                    <p className="text-green-700 font-semibold mb-2">Mot de passe réinitialisé !</p>
                    <p className="text-green-600 text-sm">
                      Redirection vers la page de connexion...
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {error && (
                      <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 animate-slide-down">
                        <p className="text-red-700 text-sm font-medium flex items-center space-x-2">
                          <span>⚠️</span>
                          <span>{error}</span>
                        </p>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        📧 Email
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white"
                        placeholder="votre@email.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        🔢 Code à 6 chiffres
                      </label>
                      <input
                        type="text"
                        required
                        maxLength={6}
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value.replace(/\D/g, '') })}
                        className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-center text-2xl font-bold tracking-widest"
                        placeholder="000000"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        🔒 Nouveau mot de passe
                      </label>
                      <PasswordInput
                        value={formData.new_password}
                        onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                        placeholder="••••••••"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        🔒 Confirmer le mot de passe
                      </label>
                      <PasswordInput
                        value={formData.new_password2}
                        onChange={(e) => setFormData({ ...formData, new_password2: e.target.value })}
                        placeholder="••••••••"
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold py-4 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                          <span>Réinitialisation...</span>
                        </>
                      ) : (
                        <>
                          <span>Réinitialiser</span>
                          <span>→</span>
                        </>
                      )}
                    </button>
                  </form>
                )}

                <div className="mt-8 text-center">
                  <Link
                    to="/login"
                    className="text-green-700 font-semibold hover:text-green-800 transition-colors text-sm"
                  >
                    ← Retour à la connexion
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Layout mobile */}
          <div className="lg:hidden w-full max-w-md">
            <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-green-100/50 p-8 sm:p-10">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-500 to-green-700 rounded-2xl mb-6 shadow-lg">
                  <span className="text-4xl">🔐</span>
                </div>
                <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-green-700 via-green-600 to-green-800 bg-clip-text text-transparent mb-3">
                  Nouveau mot de passe
                </h1>
                <p className="text-gray-600 text-lg">
                  Entrez le code reçu et votre nouveau mot de passe
                </p>
              </div>

              {success ? (
                <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 text-center">
                  <div className="text-5xl mb-4">✅</div>
                  <p className="text-green-700 font-semibold mb-2">Mot de passe réinitialisé !</p>
                  <p className="text-green-600 text-sm">
                    Redirection vers la page de connexion...
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {error && (
                    <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 animate-slide-down">
                      <p className="text-red-700 text-sm font-medium flex items-center space-x-2">
                        <span>⚠️</span>
                        <span>{error}</span>
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      📧 Email
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white"
                      placeholder="votre@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      🔢 Code à 6 chiffres
                    </label>
                    <input
                      type="text"
                      required
                      maxLength={6}
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.replace(/\D/g, '') })}
                      className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-center text-2xl font-bold tracking-widest"
                      placeholder="000000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      🔒 Nouveau mot de passe
                    </label>
                    <PasswordInput
                      value={formData.new_password}
                      onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                      placeholder="••••••••"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      🔒 Confirmer le mot de passe
                    </label>
                    <PasswordInput
                      value={formData.new_password2}
                      onChange={(e) => setFormData({ ...formData, new_password2: e.target.value })}
                      placeholder="••••••••"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold py-4 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                        <span>Réinitialisation...</span>
                      </>
                    ) : (
                      <>
                        <span>Réinitialiser</span>
                        <span>→</span>
                      </>
                    )}
                  </button>
                </form>
              )}

              <div className="mt-8 text-center">
                <Link
                  to="/login"
                  className="text-green-700 font-semibold hover:text-green-800 transition-colors text-sm"
                >
                  ← Retour à la connexion
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

